<script setup lang="ts">
import type { TableColumn, TableField } from "../../composables/Query/collectionContext";
import type { Searchable } from "../../composables/Query/searchable";
import type { createSortable } from "../../composables/Query/sortable";
import type { VTableFilterConfig } from "../../composables/Table/useTableFilters";
import type { VEmptyStateKind } from "../EmptyState/VEmptyState.types";
import type { IconName } from "../Icon/icons";
import type { VTableColumnChoice, VTableFilterPreviewCount } from "./VTable.types";
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRef,
  useId,
  useSlots,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useAppBreakpoints } from "../../composables/Breakpoints/useAppBreakpoints";
import { useCollectionContext } from "../../composables/Query/collectionContext";
import { useOptionalSearchable } from "../../composables/Query/searchable";
import { useOptionalSortable } from "../../composables/Query/sortable";
import { useColumnVisibility } from "../../composables/Table/useColumnVisibility";
import { useRefreshToast } from "../../composables/Table/useRefreshToast";
import { provideRowActionsPresentation } from "../../composables/Table/useRowActionsPresentation";
import { provideTableFilters, tryUseTableFilters } from "../../composables/Table/useTableFilters";
import GenericTableScene from "../../illustrations/empty/GenericTableScene.vue";
import VButton from "../Button/VButton.vue";
import VCheckbox from "../Checkbox/VCheckbox.vue";
import VEmptyState from "../EmptyState/VEmptyState.vue";
import VIcon from "../Icon/VIcon.vue";
import VInput from "../Input/VInput.vue";
import VSkeleton from "../Skeleton/VSkeleton.vue";
import VTooltip from "../Tooltip/VTooltip.vue";
import { columnWidthStyle, isTableColumn, tableColumnCount } from "./tableColumns";
import VTableBulkBar from "./VTableBulkBar.vue";
import VTableColumnFilter from "./VTableColumnFilter.vue";
import VTableColumnsMenu from "./VTableColumnsMenu.vue";
import VTableFilterChips from "./VTableFilterChips.vue";
import VTableFilterToolbar from "./VTableFilterToolbar.vue";
import VTableFooter from "./VTableFooter.vue";
import VTableHeadSortable from "./VTableHeadSortable.vue";
import VTableQuickFilter from "./VTableQuickFilter.vue";
import VTableToolbarButton from "./VTableToolbarButton.vue";

interface Props {
  sortableHeading?: boolean
  onRowClick?: (item: any) => void
  /** Title of the empty state. Defaults to "Nog geen <itemsLabel>". */
  emptyTitle?: string | null
  /** One line under the empty title: what appears here and how to add it. */
  emptyText?: string | null
  borderless?: boolean
  rightFieldLabel?: string | null
  rightField?: string | null
  getRowClass?: (item: any) => string | Record<string, boolean> | null
  getRowTooltip?: (item: any) => string | null
  getRowKey?: (item: any, index: number) => string | number
  selectable?: boolean
  isItemSelectable?: (item: any) => boolean
  /** Why a row cannot be selected, shown in a tooltip on its disabled checkbox. */
  getUnselectableReason?: ((item: any) => string | null) | null
  selectedIds?: Set<string>
  getItemId?: (item: any) => string
  /** Name of a row for its select checkbox, such as the item's name. Without it the row number is used. */
  getItemLabel?: (item: any) => string | null | undefined
  allSelected?: boolean
  someSelected?: boolean
  /** Clears the selection; shows "Selectie wissen" in the bulk bar. */
  onClearSelection?: (() => void) | null
  showReloadButton?: boolean
  filterableFields?: VTableFilterConfig[]
  /** Shows the Filters button (with the filter panel) and the active filters as chips in the toolbar. */
  showFilterToolbar?: boolean
  showColumnFilters?: boolean
  /** Filters (by field) that get their own button in the toolbar, with the value in the button. One to three. */
  toolbarFilters?: string[]
  /** Counts the matches of the filter panel's draft for its "Toon N" button. Without it the button says Toepassen. */
  filterPreviewCount?: VTableFilterPreviewCount
  /**
   * The search state of this table. Defaults to the provided searchable; pass one when an ancestor provides another
   * searchable under the same key (a dialog inside a list), so this table does not share it.
   */
  searchable?: Searchable | null
  /** The sort state of this table. Defaults to the provided sortable; see `searchable` for when to pass one. */
  sortable?: ReturnType<typeof createSortable> | null
  /** Puts the search field of the provided searchable in the toolbar. */
  showSearch?: boolean
  searchPlaceholder?: string | null
  /** Forces the toolbar on or off. By default it shows when it has something to show. */
  showToolbar?: boolean | null
  /** A columns button in the toolbar to show or hide columns. */
  columnsMenu?: boolean
  /** Remembers the hidden columns in this browser under this key. */
  columnsStorageKey?: string | null
  /** Shows an export button in the toolbar that calls this. */
  onExport?: (() => void) | null
  exportLoading?: boolean
  headerIcon?: IconName | null
  headerTitle?: string | null
  headerSubtitle?: string | null
  /** One row in this list ("gebruiker"), for "1 gebruiker geselecteerd". */
  itemLabel?: string | null
  /** The rows of this list ("gebruikers"), for counts and states. */
  itemsLabel?: string | null
  /**
   * Total rows on the server: the footer then shows "Geladen: 10 van 24". Defaults to the total of the provided
   * collection when it loads more while scrolling.
   */
  totalItems?: number | null
  /** Forces the footer with the count on or off. By default it shows only when `totalItems` is given. */
  showFooter?: boolean | null
  /**
   * The list is narrowed by something outside the table (a scope, a segment): an empty result then shows the
   * "no results" state instead of "nothing yet". Pair with `onClearFilters` to offer "Filters wissen".
   */
  filtered?: boolean
  onClearFilters?: (() => void) | null
  /** Skeleton rows while the first page loads. */
  skeletonRows?: number
  /** Below 768px the rows become cards. Turn off for tables that must stay a grid. */
  mobileCards?: boolean
  /**
   * Render only the rows in/near the viewport instead of all of them. Requires
   * a fixed `rowHeight`. Use for large, uniform-height lists.
   */
  virtualize?: boolean
  /** Width of the column of the `right` slot (actions). */
  rightColumnWidth?: string
  /** Fixed row height (px) used for virtualization math and row clamping. */
  rowHeight?: number
  /** Extra rows rendered above/below the viewport to avoid blank gaps. */
  virtualOverscan?: number
  /**
   * Override the element that scrolls the rows. By default the table content scrolls when its height is bounded
   * (the table fills its page); otherwise the nearest scrolling ancestor is detected. Virtualization and loading
   * more rows both follow this element.
   */
  scrollParent?: HTMLElement | null
  /**
   * Key of the group a row belongs to, such as its day. A group header row (the `group` slot) is drawn before
   * the first row of every run of rows with the same key. Return `null` for rows outside any group, such as
   * skeletons. Not for virtualized tables: header rows break the fixed row height.
   */
  groupBy?: ((item: any) => string | number | null) | null
  /** Name of a group in its header row. Defaults to the key. */
  groupLabel?: ((key: string | number) => string) | null
  /** Gray line after the group's name and count ("Gelden alleen op Demo locatie 1."). */
  groupHelp?: ((key: string | number) => string | null) | null
}

const props = withDefaults(defineProps<Props>(), {
  sortableHeading: false,
  emptyTitle: null,
  emptyText: null,
  onRowClick: () => {},
  borderless: false,
  rightFieldLabel: null,
  rightField: null,
  getRowClass: () => null,
  getRowTooltip: () => null,
  getRowKey: undefined,
  selectable: false,
  isItemSelectable: undefined,
  getUnselectableReason: null,
  selectedIds: () => new Set(),
  getItemId: () => "",
  getItemLabel: undefined,
  allSelected: false,
  someSelected: false,
  onClearSelection: null,
  showReloadButton: true,
  filterableFields: () => [],
  showFilterToolbar: false,
  showColumnFilters: false,
  toolbarFilters: () => [],
  filterPreviewCount: undefined,
  searchable: null,
  sortable: null,
  showSearch: false,
  searchPlaceholder: null,
  showToolbar: null,
  columnsMenu: false,
  columnsStorageKey: null,
  onExport: null,
  exportLoading: false,
  headerIcon: null,
  headerTitle: null,
  headerSubtitle: null,
  itemLabel: null,
  itemsLabel: null,
  totalItems: null,
  showFooter: null,
  filtered: false,
  onClearFilters: null,
  skeletonRows: 3,
  mobileCards: true,
  virtualize: false,
  rightColumnWidth: "100px",
  rowHeight: 44,
  virtualOverscan: 8,
  scrollParent: null,
  groupBy: null,
  groupLabel: null,
  groupHelp: null,
});

const emit = defineEmits<{
  toggleItem: [item: any]
  toggleAll: []
  toggleRange: [items: any[], selected: boolean]
}>();

/** Hidden columns (keys of the table fields). Bind it to control them; otherwise the table keeps them itself. */
const hiddenColumns = defineModel<string[]>("hiddenColumns", { default: () => [] });

const { t } = useI18n();
const { runRefresh } = useRefreshToast();

const lastSelectedIndex = ref<number | null>(null);

const {
  tableFields,
  loading,
  items,
  hasError,
  errorMsg,
  errorCode,
  refresh,
  isFetchingMore,
  loadMore,
} = useCollectionContext<Record<string, any>>();
const isFetchingPrevious = computed(() => loadMore?.isFetchingPrevious.value ?? false);

const existingFilters = tryUseTableFilters();
const tableFilters = existingFilters
  ?? (props.filterableFields && props.filterableFields.length > 0 ? provideTableFilters(props.filterableFields) : null);

const injectedSearchable = useOptionalSearchable();
const injectedSortable = useOptionalSortable();
/** A searchable or sortable passed as a prop wins over the provided one. */
const searchableInstance = computed(() => props.searchable ?? injectedSearchable);
const sortableInstance = computed(() => props.sortable ?? injectedSortable);
const { isPhone } = useAppBreakpoints();

const resolvedItemLabel = computed(() => props.itemLabel ?? t("base.table.item"));
const resolvedItemsLabel = computed(() => props.itemsLabel ?? t("base.table.items"));

function selectRowLabel(item: any, index: number): string {
  const name = props.getItemLabel?.(item);
  if (name) {
    return t("base.table.select_item", { name });
  }
  return t("base.table.select_row", { row: index + 1 });
}

const tableId = useId();

function idPart(value: string | number): string {
  return String(value).replace(/\s+/g, "_");
}

/** Id of the header cell of a column, unique per table, so a control in that column can be named by it. */
function headerCellId(field: string | number): string {
  return `${tableId}-header-${idPart(field)}`;
}

/** Id of the header cell above the `right` slot. */
const rightHeaderCellId = `${tableId}-header-right`;

// --- Columns -------------------------------------------------------------------------------------------------------
function columnLabel(field: TableField, key: string): string {
  if (isTableColumn(field)) {
    return field.label;
  }
  return typeof field === "string" ? field : key;
}

const allColumnKeys = computed(() => Object.keys(tableFields ?? {}));
const { isVisible, setVisible, showAll, visibleColumns } = useColumnVisibility({
  hidden: hiddenColumns,
  columns: allColumnKeys,
  storageKey: toRef(props, "columnsStorageKey"),
});

/** The table fields that are shown, in their order. */
const shownFields = computed<Record<string, TableField>>(() => {
  const fields = tableFields ?? {};
  return Object.fromEntries(Object.entries(fields).filter(([key]) => isVisible(key)));
});

const columnChoices = computed<VTableColumnChoice[]>(() =>
  Object.entries(tableFields ?? {}).map(([key, field]) => ({
    key,
    label: columnLabel(field, key),
    visible: isVisible(key),
    locked: visibleColumns.value.length <= 1 && isVisible(key),
  })),
);

/** `none` on a sortable column that is not sorted; nothing on a column that cannot be sorted. */
function ariaSortOf(field: string | number): "ascending" | "descending" | "none" | undefined {
  const sortable = sortableInstance.value;
  if (!props.sortableHeading || !sortable || !sortable.isSortable(String(field))) {
    return undefined;
  }
  if (sortable.sort.value !== field) {
    return "none";
  }
  return sortable.sortDirection.value === "DESC" ? "descending" : "ascending";
}

// --- Groups --------------------------------------------------------------------------------------------------------
function groupKeyOf(item: any): string | number | null {
  if (!props.groupBy || props.virtualize) {
    return null;
  }
  return props.groupBy(item);
}

/** Group key when this row starts a new group, otherwise null. */
function groupStartOf(index: number): string | number | null {
  const key = groupKeyOf(items.value[index]);
  if (key === null) {
    return null;
  }
  if (index > 0 && groupKeyOf(items.value[index - 1]) === key) {
    return null;
  }
  return key;
}

/** Rows in the run of rows that starts at this index. */
function groupSizeFrom(index: number): number {
  const key = groupKeyOf(items.value[index]);
  let size = 0;
  for (let next = index; next < items.value.length && groupKeyOf(items.value[next]) === key; next++) {
    size++;
  }
  return size;
}

function groupNameOf(key: string | number): string {
  return props.groupLabel ? props.groupLabel(key) : String(key);
}

function rowKeyOf(item: any, index: number): string | number {
  return props.getRowKey ? props.getRowKey(item, index) : index;
}

/** Id of a cell of a row, so a control in another cell of that row can be named by its text. */
function bodyCellId(item: any, index: number, field: string | number): string {
  return `${tableId}-row-${idPart(rowKeyOf(item, index))}-${idPart(field)}`;
}
const contentRef = useTemplateRef<HTMLDivElement>("content-ref");
const slots = useSlots();

const hasRightSlot = computed(() => !!slots.right);
const hasHeader = computed(() =>
  !!slots.header || !!props.headerIcon || !!props.headerTitle || !!props.headerSubtitle,
);

// --- Layout --------------------------------------------------------------------------------------------------------
const isCardLayout = computed(() => isPhone.value && props.mobileCards && !props.virtualize);
// Row actions in a card open as a bottom sheet.
provideRowActionsPresentation(computed(() => (isCardLayout.value ? "sheet" : "menu")));

// --- Toolbar -------------------------------------------------------------------------------------------------------
const filtersEnabled = computed(() => !!tableFilters && tableFilters.filterableFields.length > 0);
const showFilterButton = computed(() => filtersEnabled.value && props.showFilterToolbar);
const quickFilterFields = computed(() => {
  if (!tableFilters) {
    return [];
  }
  return props.toolbarFilters.filter(field => !!tableFilters.getConfig(field));
});
const showChips = computed(() =>
  filtersEnabled.value && (props.showFilterToolbar || quickFilterFields.value.length > 0),
);
const showSearchField = computed(() => props.showSearch && searchableInstance.value !== null);
const showColumnsButton = computed(() => props.columnsMenu && !isCardLayout.value && allColumnKeys.value.length > 1);
const showExportButton = computed(() => !!props.onExport);

const hasToolbarContent = computed(() =>
  showSearchField.value
  || showFilterButton.value
  || quickFilterFields.value.length > 0
  || showColumnsButton.value
  || showExportButton.value
  || !!slots.toolbar
  || !!slots["toolbar-actions"],
);
/**
 * Refresh lives in the toolbar when the toolbar has other controls. A table without them keeps it where it always was:
 * in the header of the actions column (or, on a phone, the only control of the toolbar).
 */
const refreshInToolbar = computed(() => {
  if (!props.showReloadButton) {
    return false;
  }
  return hasToolbarContent.value || props.showToolbar === true || (isCardLayout.value && hasRightSlot.value);
});
const refreshInHeader = computed(() => props.showReloadButton && !refreshInToolbar.value && hasRightSlot.value);
const toolbarVisible = computed(() => {
  if (props.showToolbar !== null) {
    return props.showToolbar;
  }
  return hasToolbarContent.value || refreshInToolbar.value;
});

const searchModel = computed<string>({
  get: () => searchableInstance.value?.searchValue.value ?? "",
  set: (value) => {
    const searchable = searchableInstance.value;
    if (searchable) {
      searchable.searchValue.value = value ?? "";
    }
  },
});
const searchPlaceholderText = computed(() => props.searchPlaceholder ?? t("base.table.search_placeholder"));
const refreshLabel = computed(() => (loading.value ? t("base.table.refreshing") : t("base.refresh")));

// --- Selection -----------------------------------------------------------------------------------------------------
const selectedCount = computed(() => props.selectedIds?.size ?? 0);
const showBulkBar = computed(() =>
  props.selectable && selectedCount.value > 0 && (!!slots["bulk-actions"] || !!props.onClearSelection),
);
const selectionLabel = computed(() => t(
  "base.table.selected",
  { count: selectedCount.value, item: resolvedItemLabel.value, items: resolvedItemsLabel.value },
  selectedCount.value,
));

function isSelected(item: any): boolean {
  return props.selectedIds?.has(props.getItemId?.(item) ?? "") ?? false;
}

// --- States --------------------------------------------------------------------------------------------------------
const searchTerm = computed(() => (searchableInstance.value?.searchValue.value ?? "").trim());
const activeFilterCount = computed(() => tableFilters?.activeFilters.value.length ?? 0);
const isNarrowed = computed(() => searchTerm.value !== "" || activeFilterCount.value > 0 || props.filtered);

const stateKind = computed<VEmptyStateKind | null>(() => {
  if (hasError.value) {
    return errorCode.value === 403 ? "no-access" : "error";
  }
  if (loading.value || items.value.length > 0) {
    return null;
  }
  return isNarrowed.value ? "no-results" : "empty";
});

const showInitialSkeleton = computed(() => loading.value && items.value.length === 0 && !hasError.value);

const emptyTitleText = computed(() => {
  if (props.emptyTitle) {
    return props.emptyTitle;
  }
  return props.itemsLabel ? t("base.table.state.empty_title", { items: props.itemsLabel }) : t("base.table.state.empty_title_generic");
});
// A table that names neither its items nor its empty title gets the generic line of the board, so it never stays bare.
const emptyTextValue = computed(() => {
  if (props.emptyText) {
    return props.emptyText;
  }
  return props.itemsLabel || props.emptyTitle || slots.emptyTitle ? "" : t("base.table.state.empty_text_generic");
});

const noResultsTitle = computed(() => {
  const items = resolvedItemsLabel.value;
  if (searchTerm.value) {
    return t("base.table.state.no_results_search_title", { items, search: searchTerm.value });
  }
  return t("base.table.state.no_results_title", { items });
});

const canClearFilters = computed(() => activeFilterCount.value > 0 || (props.filtered && !!props.onClearFilters));
const noResultsText = computed(() => {
  if (searchTerm.value && canClearFilters.value && activeFilterCount.value > 0) {
    return t("base.table.state.no_results_search_and_filters", { count: activeFilterCount.value }, activeFilterCount.value);
  }
  return t("base.table.state.no_results_text");
});

function clearSearch() {
  searchableInstance.value?.clearSearch();
}

function clearAllFilters() {
  tableFilters?.clearFilters();
  props.onClearFilters?.();
}

const errorTitleText = computed(() => {
  if (stateKind.value === "no-access") {
    return props.itemsLabel ? t("base.table.state.no_access_title", { items: props.itemsLabel }) : t("base.table.state.no_access_title_generic");
  }
  return props.itemsLabel ? t("base.table.state.error_title", { items: props.itemsLabel }) : t("base.table.state.error_title_generic");
});
const errorText = computed(() => {
  if (stateKind.value !== "no-access") {
    return t("base.table.state.error_text");
  }
  return props.itemsLabel ? t("base.table.state.no_access_text") : t("base.table.state.no_access_text_generic");
});
const errorDetail = computed(() => {
  if (stateKind.value !== "error" || !errorCode.value) {
    return "";
  }
  return t("base.table.state.error_code", { code: errorCode.value });
});

// --- Footer --------------------------------------------------------------------------------------------------------
const footerTotal = computed<number | null>(() => {
  if (props.totalItems !== null) {
    return props.totalItems;
  }
  // The collection knows its total once the first page is in.
  if (!loadMore || loading.value || hasError.value) {
    return null;
  }
  return loadMore.totalItems.value;
});

const footerVisible = computed(() => {
  if (props.showFooter !== null) {
    return props.showFooter;
  }
  return props.totalItems !== null;
});

// --- Scroll container ----------------------------------------------------------------------------------------------
// The element that actually scrolls the rows. It is `.v-table-content` itself when the table fills a bounded height
// (the page does not scroll), or an ancestor (dialog or tab body, a page that scrolls) when the content grows freely.
const scrollEl = ref<HTMLElement | null>(null);
// How far the data rows have scrolled above the visible area (px).
const scrolledPx = ref(0);
// Height of the visible viewport (the real scroll container's clientHeight).
const viewportHeight = ref(0);
let contentResizeObserver: ResizeObserver | null = null;

const bodyColSpan = computed(() => tableColumnCount(shownFields.value, {
  selectable: props.selectable,
  hasRightSlot: hasRightSlot.value,
}));

// Absolute-indexed rows so selection / keys stay stable regardless of the
// rendered window.
const indexedItems = computed(() =>
  items.value.map((item, index) => ({ item, index })),
);

const virtualStart = computed(() => {
  if (!props.virtualize) {
    return 0;
  }
  return Math.max(0, Math.floor(scrolledPx.value / props.rowHeight) - props.virtualOverscan);
});

const virtualEnd = computed(() => {
  if (!props.virtualize) {
    return items.value.length;
  }
  const visibleCount = Math.ceil(viewportHeight.value / props.rowHeight) + props.virtualOverscan * 2;
  return Math.min(items.value.length, virtualStart.value + visibleCount);
});

const renderRows = computed(() =>
  props.virtualize
    ? indexedItems.value.slice(virtualStart.value, virtualEnd.value)
    : indexedItems.value,
);

const topSpacerHeight = computed(() =>
  props.virtualize ? virtualStart.value * props.rowHeight : 0,
);
const bottomSpacerHeight = computed(() =>
  props.virtualize ? (items.value.length - virtualEnd.value) * props.rowHeight : 0,
);

function isScrollableY(el: HTMLElement): boolean {
  const overflowY = getComputedStyle(el).overflowY;
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

// Resolve the element that genuinely scrolls the rows. Prefer `.v-table-content`
// when it overflows its own box; otherwise climb to the nearest ancestor that
// actually scrolls. The spacer rows reserve the full list height, so whichever
// element is height-bounded will be the one that overflows.
function resolveScrollElement(): HTMLElement | null {
  if (props.scrollParent) {
    return props.scrollParent;
  }
  const content = contentRef.value;
  if (!content) {
    return null;
  }
  if (content.scrollHeight - content.clientHeight > 1 && isScrollableY(content)) {
    return content;
  }
  let el: HTMLElement | null = content.parentElement;
  while (el && el !== document.body && el !== document.documentElement) {
    if (el.scrollHeight - el.clientHeight > 1 && isScrollableY(el)) {
      return el;
    }
    el = el.parentElement;
  }
  return content;
}

/** Room (px) between the rows and the visible area of the scroller: above the first row and below the last one. */
function rowsOutsideView(scroller: HTMLElement, content: HTMLElement): { above: number, below: number } {
  if (scroller === content) {
    return {
      above: scroller.scrollTop,
      below: scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight,
    };
  }
  const contentRect = content.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  return {
    above: Math.max(0, scrollerRect.top - contentRect.top),
    below: Math.max(0, contentRect.bottom - scrollerRect.bottom),
  };
}

function measureVirtual() {
  if (!props.virtualize) {
    return;
  }
  const scroller = scrollEl.value;
  const content = contentRef.value;
  if (!scroller || !content) {
    return;
  }
  viewportHeight.value = scroller.clientHeight;
  if (scroller === content) {
    scrolledPx.value = scroller.scrollTop;
    return;
  }
  // `tableTopY` = offset of the table content from the top of the scroller's
  // scrollable area (invariant of scroll position). The thead height cancels
  // out of the start index, and `virtualOverscan` absorbs any sub-row error.
  const tableTopY = (content.getBoundingClientRect().top - scroller.getBoundingClientRect().top) + scroller.scrollTop;
  scrolledPx.value = Math.max(0, scroller.scrollTop - tableTopY);
}

// --- Loading more while scrolling ----------------------------------------------------------------------------------
// Rows load a page ahead: the next page starts loading when less than half a screen (at least 200px) of rows is left.
const LOAD_MORE_MIN_DISTANCE_PX = 200;
// Row count when the last page was asked for. A load that added nothing (it failed) is not retried by itself, only
// by scrolling again, so a failing page cannot loop.
let rowCountAtLastLoad: number | null = null;

function loadMoreWhenNearEdge(fromScroll: boolean) {
  const scroller = scrollEl.value;
  const content = contentRef.value;
  if (!loadMore || !scroller || !content) {
    return;
  }
  // A hidden table (a closed tab) has no height and would load every page.
  if (scroller.clientHeight === 0) {
    return;
  }
  if (loading.value || hasError.value || isFetchingMore.value || isFetchingPrevious.value) {
    return;
  }
  if (!fromScroll && rowCountAtLastLoad === items.value.length) {
    return;
  }
  const distance = Math.max(LOAD_MORE_MIN_DISTANCE_PX, scroller.clientHeight / 2);
  const { above, below } = rowsOutsideView(scroller, content);
  if (loadMore.hasNextPage.value && below < distance) {
    rowCountAtLastLoad = items.value.length;
    void loadMore.fetchNextPage();
    return;
  }
  if (loadMore.hasPreviousPage.value && above < distance) {
    rowCountAtLastLoad = items.value.length;
    void loadMore.fetchPreviousPage();
  }
}

watch(loading, (isLoading) => {
  // A new list (other search, filter or scope) starts over.
  if (isLoading) {
    rowCountAtLastLoad = null;
  }
});

// The list keeps a window of pages: loading a page at one end can drop one at the other, and a page loaded above
// pushes the rows down. Keep the row the user looks at in place.
function anchorIdOf(item: any): string | null {
  if (!item || typeof item !== "object" || "skeleton" in item) {
    return null;
  }
  const id = item["@id"] ?? props.getItemId?.(item);
  return id ? String(id) : null;
}

interface ScrollAnchor {
  id: string
  index: number
  offset: number
  scrollTop: number
}

function captureAnchor(): ScrollAnchor | null {
  const scroller = scrollEl.value;
  const content = contentRef.value;
  if (!scroller || !content) {
    return null;
  }
  const scrollerTop = scroller === content
    ? content.getBoundingClientRect().top
    : scroller.getBoundingClientRect().top;
  for (const row of content.querySelectorAll<HTMLElement>("[data-row-anchor]")) {
    const rect = row.getBoundingClientRect();
    if (rect.bottom <= scrollerTop) {
      continue;
    }
    const id = row.dataset.rowAnchor ?? "";
    const index = items.value.findIndex(item => anchorIdOf(item) === id);
    return { id, index, offset: rect.top - scrollerTop, scrollTop: scroller.scrollTop };
  }
  return null;
}

function restoreAnchor(anchor: ScrollAnchor) {
  const scroller = scrollEl.value;
  const content = contentRef.value;
  if (!scroller || !content) {
    return;
  }
  const newIndex = items.value.findIndex(item => anchorIdOf(item) === anchor.id);
  if (newIndex === -1) {
    return;
  }
  if (props.virtualize && anchor.index !== -1) {
    // Fixed row height: the anchor moved by whole rows.
    scroller.scrollTop = anchor.scrollTop + (newIndex - anchor.index) * props.rowHeight;
    return;
  }
  const row = [...content.querySelectorAll<HTMLElement>("[data-row-anchor]")].find(el => el.dataset.rowAnchor === anchor.id);
  if (!row) {
    return;
  }
  const scrollerTop = scroller === content
    ? content.getBoundingClientRect().top
    : scroller.getBoundingClientRect().top;
  scroller.scrollTop += (row.getBoundingClientRect().top - scrollerTop) - anchor.offset;
}

watch(
  [items, isFetchingPrevious],
  ([nextItems, nextFetchingPrevious], [previousItems, previousFetchingPrevious]) => {
    if (!loadMore || previousItems.length === 0) {
      return;
    }
    const firstChanged = anchorIdOf(nextItems[0]) !== anchorIdOf(previousItems[0]);
    if (!firstChanged && nextFetchingPrevious === previousFetchingPrevious) {
      return;
    }
    const anchor = captureAnchor();
    if (anchor) {
      nextTick(() => restoreAnchor(anchor));
    }
  },
  { flush: "pre" },
);

function onScroll() {
  measureVirtual();
  loadMoreWhenNearEdge(true);
}

function bindScrollEl(next: HTMLElement | null) {
  if (scrollEl.value === next) {
    return;
  }
  scrollEl.value?.removeEventListener("scroll", onScroll);
  scrollEl.value = next;
  scrollEl.value?.addEventListener("scroll", onScroll, { passive: true });
}

// Re-detect the scroll container (it can change once data fills the layout), re-measure and fill the visible area:
// a first page that does not reach the bottom loads the next one without a scroll. Cheap enough to run on mount,
// data changes and resizes.
function refreshScrolling() {
  bindScrollEl(resolveScrollElement());
  measureVirtual();
  loadMoreWhenNearEdge(false);
}

// Expose the contentRef to parent components
defineExpose({
  contentRef,
});

onMounted(() => {
  nextTick(refreshScrolling);
  if (contentRef.value) {
    contentResizeObserver = new ResizeObserver(() => {
      refreshScrolling();
    });
    contentResizeObserver.observe(contentRef.value);
  }
  window.addEventListener("resize", refreshScrolling, { passive: true });
});

onUnmounted(() => {
  contentResizeObserver?.disconnect();
  scrollEl.value?.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", refreshScrolling);
});

// Data length changes the content height (and thus which element overflows /
// scrolls), so re-resolve the scroll container after the DOM settles.
watch(() => items.value.length, () => {
  nextTick(refreshScrolling);
});
watch([loading, isFetchingMore, isFetchingPrevious], () => {
  nextTick(refreshScrolling);
});

function isSelectable(item: any): boolean {
  return !props.isItemSelectable || props.isItemSelectable(item);
}

function unselectableReason(item: any): string | null {
  return isSelectable(item) ? null : props.getUnselectableReason?.(item) ?? null;
}

function handleCheckboxClick(item: any, index: number, event: MouseEvent) {
  if (!isSelectable(item)) {
    return;
  }
  if (event.shiftKey && lastSelectedIndex.value !== null) {
    const start = Math.min(lastSelectedIndex.value, index);
    const end = Math.max(lastSelectedIndex.value, index);
    const rangeItems = items.value.slice(start, end + 1).filter(isSelectable);

    const anchorItemId = props.getItemId?.(items.value[lastSelectedIndex.value]) ?? "";
    const isAnchorSelected = props.selectedIds?.has(anchorItemId);

    emit("toggleRange", rangeItems, isAnchorSelected);
  } else {
    emit("toggleItem", item);
  }

  lastSelectedIndex.value = index;
}

/**
 * Whether the screen handles a row click. The prop defaults to a no-op, so only a handler the parent passed makes
 * rows interactive (pointer cursor, focusable, Enter and Space).
 */
const instance = getCurrentInstance();
const rowsClickable = computed(() => {
  const vnodeProps = instance?.vnode.props ?? {};
  const passed = "onRowClick" in vnodeProps || "on-row-click" in vnodeProps;
  return passed && typeof props.onRowClick === "function" && !loading.value;
});

function activateRow(item: any) {
  if (!rowsClickable.value) {
    return;
  }
  props.onRowClick?.(item);
}

/** Enter and Space on the focused row itself open it; keys in a control inside the row stay with that control. */
function onRowKeydown(event: KeyboardEvent, item: any) {
  if (event.target !== event.currentTarget || !rowsClickable.value) {
    return;
  }
  if (event.key !== "Enter" && event.key !== " ") {
    return;
  }
  event.preventDefault();
  activateRow(item);
}

/** A card's checkbox toggles its row; range selection with shift is a desktop gesture. */
function toggleFromCard(item: any, index: number) {
  if (!isSelectable(item)) {
    return;
  }
  emit("toggleItem", item);
  lastSelectedIndex.value = index;
}

function isFieldFilterable(fieldName: string): boolean {
  return props.filterableFields?.some(f => f.field === fieldName) ?? false;
}

const firstShownField = computed(() => Object.keys(shownFields.value)[0] ?? null);
const otherShownFields = computed(() => Object.entries(shownFields.value).slice(1));
</script>

<template>
  <div
    class="v-table-container"
    :class="[
      borderless ? 'border-none' : 'rounded-xl border border-gray-300 bg-white dark:border-dark-600 dark:bg-dark-800 overflow-hidden',
    ]"
  >
    <header
      v-if="hasHeader"
      class="v-table-section-header flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-gray-200 dark:border-dark-600 shrink-0"
    >
      <slot name="header">
        <div class="flex items-center gap-2 min-w-0">
          <VIcon
            v-if="headerIcon"
            :name="headerIcon"
            class="text-primary-500 text-sm shrink-0"
            aria-hidden="true"
          />
          <h2 v-if="headerTitle" class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {{ headerTitle }}
          </h2>
        </div>
        <span
          v-if="headerSubtitle"
          class="text-xs text-gray-500 dark:text-gray-400 truncate"
        >
          {{ headerSubtitle }}
        </span>
      </slot>
    </header>

    <!-- Toolbar: never wraps on desktop. Actions stay right; the search shrinks to 140px and the chips scroll. -->
    <div
      v-if="toolbarVisible"
      class="v-table-toolbar shrink-0 bg-gray-50 px-3.5 py-2.5 text-[13px] text-gray-700 dark:bg-dark-700 dark:text-gray-400"
      :class="[
        isCardLayout ? 'flex flex-col gap-2' : 'flex flex-nowrap items-center gap-2',
        { 'border-t border-gray-200 dark:border-dark-600': hasHeader },
      ]"
      data-testid="table-toolbar"
    >
      <div :class="isCardLayout ? 'flex items-center gap-2' : 'contents'">
        <div
          v-if="showSearchField"
          class="v-table-search min-w-[140px]"
          :class="isCardLayout ? 'min-w-0 flex-1' : 'flex-[0_1_300px]'"
        >
          <VInput
            v-model="searchModel"
            type="search"
            :size="isCardLayout ? 'large' : 'medium'"
            icon-left="search"
            :placeholder="searchPlaceholderText"
            :aria-label="searchPlaceholderText"
            clearable
            data-testid="table-search"
          />
        </div>
        <VTableFilterToolbar
          v-if="showFilterButton"
          :preview-count="filterPreviewCount"
          :item-label="resolvedItemLabel"
          :items-label="resolvedItemsLabel"
          :large="isCardLayout"
        />
        <template v-if="!isCardLayout">
          <VTableQuickFilter v-for="field in quickFilterFields" :key="field" :field="field" />
          <slot name="toolbar" />
          <VTableFilterChips v-if="showChips" />
          <span class="min-w-2 flex-1" aria-hidden="true" />
        </template>
        <span v-else-if="!showSearchField" class="flex-1" aria-hidden="true" />
        <slot name="toolbar-actions" />
        <VTableColumnsMenu
          v-if="showColumnsButton"
          :columns="columnChoices"
          @toggle="setVisible"
          @show-all="showAll"
        />
        <VTableToolbarButton
          v-if="showExportButton"
          icon="arrow-down-to-line"
          :label="t('base.table.export')"
          :loading="exportLoading"
          :large="isCardLayout"
          icon-only
          data-testid="table-export"
          @click="onExport?.()"
        />
        <VTableToolbarButton
          v-if="refreshInToolbar"
          icon="refresh-cw"
          :label="refreshLabel"
          :loading="loading"
          :large="isCardLayout"
          icon-only
          data-testid="table-refresh"
          @click="runRefresh(refresh)"
        />
      </div>
      <div
        v-if="isCardLayout && (quickFilterFields.length > 0 || slots.toolbar || showChips)"
        class="flex min-w-0 items-center gap-2 overflow-x-auto"
      >
        <VTableQuickFilter v-for="field in quickFilterFields" :key="field" :field="field" />
        <slot name="toolbar" />
        <VTableFilterChips v-if="showChips" large />
      </div>
    </div>

    <VTableBulkBar
      v-if="showBulkBar"
      :label="selectionLabel"
      :can-clear="!!onClearSelection"
      @clear="onClearSelection?.()"
    >
      <slot name="bulk-actions" :count="selectedCount" />
    </VTableBulkBar>

    <div
      ref="content-ref"
      class="v-table-content relative"
      :class="{ 'border-t border-gray-200 dark:border-dark-600': toolbarVisible || hasHeader || showBulkBar }"
      :aria-busy="loading || isFetchingMore || isFetchingPrevious || undefined"
    >
      <span v-if="showInitialSkeleton" class="sr-only" role="status">{{ t("base.loading") }}</span>

      <!-- Phones: every row is a card. -->
      <ul v-if="isCardLayout" class="v-table-cards m-0 flex list-none flex-col p-0" data-testid="table-cards">
        <template v-if="showInitialSkeleton">
          <li v-for="n in skeletonRows" :key="`skeleton-${n}`" class="flex items-center gap-3 border-t border-gray-200 px-3.5 py-3 first:border-t-0 dark:border-dark-600" aria-hidden="true">
            <VSkeleton :height="40" :width="40" width-unit="px" :dynamic-width="false" />
            <div class="flex flex-1 flex-col gap-2">
              <VSkeleton :height="14" :width="60" :dynamic-width="false" />
              <VSkeleton :height="12" :width="40" :dynamic-width="false" />
            </div>
          </li>
        </template>
        <template v-else-if="stateKind === null">
          <li v-for="n in isFetchingPrevious ? 3 : 0" :key="`fetch-previous-${n}`" class="flex items-center gap-3 border-t border-gray-200 px-3.5 py-3 first:border-t-0 dark:border-dark-600" aria-hidden="true" data-testid="table-loading-previous">
            <VSkeleton :height="40" :width="40" width-unit="px" :dynamic-width="false" />
            <VSkeleton :height="14" :width="60" :dynamic-width="false" />
          </li>
          <template v-for="{ item, index } in renderRows" :key="rowKeyOf(item, index)">
            <li
              v-if="groupStartOf(index) !== null"
              class="flex items-center gap-2 border-t border-gray-200 bg-gray-50 px-3.5 py-2 text-xs font-medium text-gray-700 first:border-t-0 dark:border-dark-600 dark:bg-dark-700 dark:text-gray-400"
            >
              <span>{{ groupNameOf(groupStartOf(index)!) }}</span>
              <span class="text-primary-600 dark:text-primary-400">{{ groupSizeFrom(index) }}</span>
            </li>
            <li
              class="v-table-card relative grid items-center gap-3 border-t border-gray-200 py-3 pr-1.5 pl-3.5 first:border-t-0 dark:border-dark-600"
              :class="[
                selectable ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]',
                { 'bg-primary-50/60 dark:bg-primary-900/20': selectable && isSelected(item) },
                { 'is-clickable cursor-pointer': rowsClickable },
                getRowClass?.(item),
              ]"
              :tabindex="rowsClickable ? 0 : undefined"
              :data-row-anchor="anchorIdOf(item) ?? undefined"
              @click="activateRow(item)"
              @keydown="(event: KeyboardEvent) => onRowKeydown(event, item)"
            >
              <div
                v-if="selectable"
                v-tooltip="{ content: unselectableReason(item), placement: 'top', theme: 'verbleif-tooltip-solid' }"
                class="flex"
                @click.stop
              >
                <VCheckbox
                  :model-value="isSelected(item)"
                  :disabled="!isSelectable(item)"
                  :name="`select-${getItemId?.(item) ?? ''}`"
                  :aria-label="selectRowLabel(item, index)"
                  @update:model-value="toggleFromCard(item, index)"
                />
              </div>
              <div class="flex min-w-0 flex-col gap-1.5">
                <slot name="mobile-row" :item="item as any" :loading="loading">
                  <div v-if="firstShownField" class="min-w-0 truncate text-[15px] font-medium">
                    <slot
                      :field="firstShownField"
                      :loading="loading"
                      :name="firstShownField"
                      :item="item as any"
                      :header-id="headerCellId(firstShownField)"
                      :cell-id="(otherField: string) => bodyCellId(item, index, otherField)"
                    >
                      {{ item[firstShownField] }}
                    </slot>
                  </div>
                  <dl v-if="otherShownFields.length > 0" class="m-0 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    <div v-for="[field, column] in otherShownFields" :key="field" class="flex min-w-0 items-center gap-1">
                      <dt class="text-gray-600 dark:text-gray-400">
                        {{ columnLabel(column, field) }}
                      </dt>
                      <dd class="m-0 min-w-0 truncate">
                        <slot
                          :field="field"
                          :loading="loading"
                          :name="field"
                          :item="item as any"
                          :header-id="headerCellId(field)"
                          :cell-id="(otherField: string) => bodyCellId(item, index, otherField)"
                        >
                          {{ item[field] }}
                        </slot>
                      </dd>
                    </div>
                  </dl>
                </slot>
              </div>
              <div v-if="hasRightSlot" class="flex items-center justify-end" @click.stop>
                <slot
                  :loading="loading"
                  name="right"
                  :item="item as any"
                  :header-id="rightHeaderCellId"
                  :cell-id="(otherField: string) => bodyCellId(item, index, otherField)"
                />
              </div>
            </li>
          </template>
          <li v-for="n in isFetchingMore ? 3 : 0" :key="`fetch-more-${n}`" class="flex items-center gap-3 border-t border-gray-200 px-3.5 py-3 dark:border-dark-600" aria-hidden="true" data-testid="table-loading-more">
            <VSkeleton :height="40" :width="40" width-unit="px" :dynamic-width="false" />
            <VSkeleton :height="14" :width="60" :dynamic-width="false" />
          </li>
        </template>
      </ul>

      <table v-else class="v-table" :class="{ 'is-virtualized': virtualize }">
        <colgroup>
          <col v-if="selectable" style="width: 40px">
          <col
            v-for="(field, fieldName) in shownFields"
            :key="fieldName"
            :style="columnWidthStyle(field as TableField)"
          >
          <col v-if="hasRightSlot" :style="{ width: rightColumnWidth }">
        </colgroup>
        <thead>
          <tr class="v-table-header">
            <th
              v-if="selectable"
              scope="col"
              class="v-table-cell v-table-header-cell v-table-select-cell"
            >
              <VCheckbox
                :model-value="allSelected"
                :indeterminate="someSelected"
                :disabled="items.length === 0"
                name="select-all"
                :aria-label="t('base.select_all')"
                @update:model-value="emit('toggleAll')"
              />
            </th>
            <th
              v-for="(field, fieldName) in shownFields"
              :id="headerCellId(fieldName)"
              :key="fieldName"
              scope="col"
              class="v-table-cell v-table-header-cell"
              :class="{ 'is-right': isTableColumn(field as TableField) && (field as TableColumn).align === 'right' }"
              :aria-sort="ariaSortOf(fieldName)"
            >
              <div class="flex items-center gap-2 w-full">
                <VTableHeadSortable
                  :item="{
                    field: fieldName,
                    translation: columnLabel(field as TableField, String(fieldName)),
                  }"
                  :sortable-heading="sortableHeading"
                  :sortable="sortableInstance"
                />
                <VTooltip
                  v-if="isTableColumn(field as TableField) && (field as TableColumn).hint"
                  :content="(field as TableColumn).hint"
                >
                  <template #reference>
                    <VIcon
                      name="info"
                      class="text-gray-400 dark:text-gray-500 cursor-help"
                      role="img"
                      :aria-label="(field as TableColumn).hint"
                    />
                  </template>
                </VTooltip>
                <VTableColumnFilter
                  v-if="showColumnFilters && isFieldFilterable(String(fieldName))"
                  :field="String(fieldName)"
                />
              </div>
            </th>
            <th
              v-if="hasRightSlot"
              :id="rightHeaderCellId"
              scope="col"
              class="v-table-cell v-table-header-cell v-table-actions-cell is-right"
              :aria-sort="rightField ? ariaSortOf(rightField) : undefined"
            >
              <div v-if="refreshInHeader" class="flex items-center justify-end">
                <VTableToolbarButton
                  icon="refresh-cw"
                  :label="refreshLabel"
                  :loading="loading"
                  icon-only
                  data-testid="table-refresh"
                  @click="runRefresh(refresh)"
                />
              </div>
              <VTableHeadSortable
                v-if="rightField && rightFieldLabel"
                :item="{
                  field: rightField,
                  translation: rightFieldLabel,
                }"
                right
                :sortable-heading="sortableHeading"
                :sortable="sortableInstance"
              />
              <span v-else-if="!refreshInHeader" class="sr-only">{{ t("base.table.actions") }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="stateKind !== null" class="v-table-row v-table-state" :class="`v-table-${stateKind}`">
            <td :colspan="bodyColSpan" class="v-table-state-cell">
              <!-- Error and no access -->
              <slot
                v-if="stateKind === 'error' || stateKind === 'no-access'"
                :error-code="errorCode"
                :error-msg="errorMsg"
                name="error"
              >
                <VEmptyState
                  :title="errorTitleText"
                  :text="errorText"
                  :detail="errorDetail"
                  :state="stateKind"
                  variant="plain"
                  :heading-level="3"
                  live="alert"
                >
                  <template v-if="slots.errorIcon" #illustration>
                    <slot :error-code="errorCode" :error-msg="errorMsg" name="errorIcon" />
                  </template>
                  <template v-else #scene>
                    <slot name="scene" :state="stateKind">
                      <GenericTableScene />
                    </slot>
                  </template>
                  <template v-if="slots.errorTitle" #title>
                    <slot :error-code="errorCode" :error-msg="errorMsg" name="errorTitle" />
                  </template>
                  <template v-if="slots.errorText" #default>
                    <slot :error-code="errorCode" :error-msg="errorMsg" name="errorText" />
                  </template>
                  <template v-if="stateKind === 'error'" #actions>
                    <VButton variant="gray" rounded icon-left="refresh-cw" data-testid="table-retry" @click="runRefresh(refresh)">
                      {{ t("base.table.state.retry") }}
                    </VButton>
                  </template>
                </VEmptyState>
              </slot>

              <!-- No results for the search or filters -->
              <slot
                v-else-if="stateKind === 'no-results'"
                name="noResults"
                :search="searchTerm"
                :filter-count="activeFilterCount"
                :clear-search="clearSearch"
                :clear-filters="clearAllFilters"
              >
                <VEmptyState
                  :title="noResultsTitle"
                  :text="noResultsText"
                  state="no-results"
                  variant="plain"
                  :heading-level="3"
                  live="status"
                >
                  <template #scene>
                    <slot name="scene" state="no-results">
                      <GenericTableScene />
                    </slot>
                  </template>
                  <template v-if="searchTerm || canClearFilters" #actions>
                    <VButton v-if="searchTerm" variant="gray" rounded data-testid="table-clear-search" @click="clearSearch">
                      {{ t("base.table.state.clear_search") }}
                    </VButton>
                    <VButton v-if="canClearFilters" variant="light" rounded data-testid="table-clear-filters" @click="clearAllFilters">
                      {{ t("base.table.state.clear_filters") }}
                    </VButton>
                  </template>
                </VEmptyState>
              </slot>

              <!-- Nothing yet -->
              <slot v-else name="empty">
                <VEmptyState
                  :title="emptyTitleText"
                  :text="emptyTextValue"
                  state="empty"
                  variant="plain"
                  :heading-level="3"
                >
                  <template v-if="slots.emptyIcon" #illustration>
                    <slot name="emptyIcon" />
                  </template>
                  <template v-else #scene>
                    <slot name="scene" state="empty">
                      <GenericTableScene />
                    </slot>
                  </template>
                  <template v-if="slots.emptyTitle" #title>
                    <slot name="emptyTitle" />
                  </template>
                  <template v-if="slots.emptyText" #default>
                    <slot name="emptyText" />
                  </template>
                  <template v-if="slots['empty-actions']" #actions>
                    <slot name="empty-actions" />
                  </template>
                </VEmptyState>
              </slot>
            </td>
          </tr>
          <template v-else-if="showInitialSkeleton">
            <tr v-for="n in skeletonRows" :key="`skeleton-${n}`" class="v-table-row" aria-hidden="true">
              <td v-if="selectable" class="v-table-cell v-table-select-cell">
                <VSkeleton :height="18" :width="18" width-unit="px" :dynamic-width="false" />
              </td>
              <td v-for="(_, field) in shownFields" :key="field" class="v-table-cell">
                <VSkeleton :height="12" :width="60" />
              </td>
              <td v-if="hasRightSlot" class="v-table-cell is-right">
                <VSkeleton :height="20" :width="20" width-unit="px" :dynamic-width="false" />
              </td>
            </tr>
          </template>
          <template v-else>
            <tr
              v-for="n in isFetchingPrevious ? 3 : 0"
              :key="`fetch-previous-${n}`"
              class="v-table-row"
              aria-hidden="true"
              data-testid="table-loading-previous"
            >
              <td :colspan="bodyColSpan" class="v-table-cell">
                <VSkeleton :height="16" :width="50" />
              </td>
            </tr>
            <tr v-if="virtualize && topSpacerHeight > 0" aria-hidden="true" class="v-table-spacer">
              <td :colspan="bodyColSpan" :style="{ height: `${topSpacerHeight}px`, padding: 0, border: 'none' }" />
            </tr>
            <template v-for="{ item, index } in renderRows" :key="rowKeyOf(item, index)">
              <tr
                v-if="groupStartOf(index) !== null"
                class="v-table-row v-table-group-row"
              >
                <slot
                  name="group"
                  :group="groupStartOf(index)!"
                  :item="item as any"
                  :colspan="bodyColSpan"
                  :count="groupSizeFrom(index)"
                >
                  <th :colspan="bodyColSpan" scope="colgroup" class="v-table-cell v-table-group-cell">
                    <span class="flex items-center gap-2">
                      <span>{{ groupNameOf(groupStartOf(index)!) }}</span>
                      <span class="font-medium text-primary-600 dark:text-primary-400">{{ groupSizeFrom(index) }}</span>
                      <span v-if="groupHelp?.(groupStartOf(index)!)" class="ml-1 font-normal text-gray-600 dark:text-gray-400">
                        {{ groupHelp?.(groupStartOf(index)!) }}
                      </span>
                    </span>
                  </th>
                </slot>
              </tr>
              <tr
                v-tooltip="{
                  content: getRowTooltip?.(item),
                  placement: 'top',
                  theme: 'verbleif-tooltip-solid',
                }"
                class="v-table-row v-table-body-row"
                :class="[
                  { 'is-clickable': rowsClickable },
                  { 'is-selected': selectable && isSelected(item) },
                  getRowClass?.(item),
                ]"
                :style="virtualize ? { height: `${rowHeight}px` } : undefined"
                :tabindex="rowsClickable ? 0 : undefined"
                :data-row-anchor="anchorIdOf(item) ?? undefined"
                @click="activateRow(item)"
                @keydown="(event: KeyboardEvent) => onRowKeydown(event, item)"
              >
                <td
                  v-if="selectable && !('skeleton' in item)"
                  v-tooltip="{ content: unselectableReason(item), placement: 'top', theme: 'verbleif-tooltip-solid' }"
                  class="v-table-cell v-table-select-cell"
                  :class="{ 'cursor-pointer': isSelectable(item), 'cursor-not-allowed opacity-60': !isSelectable(item) }"
                  @mousedown.prevent
                  @click.stop="(e) => handleCheckboxClick(item, index, e)"
                >
                  <div class="pointer-events-none">
                    <VCheckbox
                      :model-value="isSelected(item)"
                      :disabled="!isSelectable(item)"
                      :name="`select-${getItemId?.(item) ?? ''}`"
                      :aria-label="selectRowLabel(item, index)"
                    />
                  </div>
                </td>
                <td
                  v-for="(_, field) in shownFields"
                  :id="bodyCellId(item, index, field)"
                  :key="field"
                  class="v-table-cell"
                  :class="field"
                >
                  <slot
                    :field="field"
                    :loading="loading"
                    :name="field"
                    :item="item as any"
                    :header-id="headerCellId(field)"
                    :cell-id="(otherField: string) => bodyCellId(item, index, otherField)"
                  >
                    <VSkeleton v-if="loading" :height="16" :width="50" />
                    <template v-else>
                      {{ item[field as keyof typeof item] }}
                    </template>
                  </slot>
                </td>
                <td v-if="hasRightSlot" class="v-table-cell is-right">
                  <slot
                    :loading="loading"
                    name="right"
                    :item="item as any"
                    :header-id="rightHeaderCellId"
                    :cell-id="(otherField: string) => bodyCellId(item, index, otherField)"
                  />
                </td>
              </tr>
            </template>
            <tr
              v-for="n in isFetchingMore ? 3 : 0"
              :key="`fetch-more-${n}`"
              class="v-table-row"
              aria-hidden="true"
              data-testid="table-loading-more"
            >
              <td
                v-if="selectable"
                class="v-table-cell v-table-select-cell"
              />
              <td
                v-for="(_, field) in shownFields"
                :key="field"
                class="v-table-cell"
              >
                <VSkeleton :height="16" :width="50" />
              </td>
              <td
                v-if="hasRightSlot"
                class="v-table-cell is-right"
              />
            </tr>
            <tr v-if="virtualize && bottomSpacerHeight > 0" aria-hidden="true" class="v-table-spacer">
              <td :colspan="bodyColSpan" :style="{ height: `${bottomSpacerHeight}px`, padding: 0, border: 'none' }" />
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Phones: the states sit under the toolbar, outside the card list. They take the same slots as the table. -->
      <div v-if="isCardLayout && stateKind !== null" class="v-table-state">
        <slot
          v-if="stateKind === 'error' || stateKind === 'no-access'"
          :error-code="errorCode"
          :error-msg="errorMsg"
          name="error"
        >
          <VEmptyState
            :title="errorTitleText"
            :text="errorText"
            :detail="errorDetail"
            :state="stateKind"
            variant="plain"
            :heading-level="3"
            live="alert"
          >
            <template v-if="slots.errorIcon" #illustration>
              <slot :error-code="errorCode" :error-msg="errorMsg" name="errorIcon" />
            </template>
            <template v-else #scene>
              <slot name="scene" :state="stateKind">
                <GenericTableScene />
              </slot>
            </template>
            <template v-if="slots.errorTitle" #title>
              <slot :error-code="errorCode" :error-msg="errorMsg" name="errorTitle" />
            </template>
            <template v-if="slots.errorText" #default>
              <slot :error-code="errorCode" :error-msg="errorMsg" name="errorText" />
            </template>
            <template v-if="stateKind === 'error'" #actions>
              <VButton variant="gray" rounded icon-left="refresh-cw" data-testid="table-retry" @click="runRefresh(refresh)">
                {{ t("base.table.state.retry") }}
              </VButton>
            </template>
          </VEmptyState>
        </slot>
        <slot
          v-else-if="stateKind === 'no-results'"
          name="noResults"
          :search="searchTerm"
          :filter-count="activeFilterCount"
          :clear-search="clearSearch"
          :clear-filters="clearAllFilters"
        >
          <VEmptyState
            :title="noResultsTitle"
            :text="noResultsText"
            state="no-results"
            variant="plain"
            :heading-level="3"
            live="status"
          >
            <template #scene>
              <slot name="scene" state="no-results">
                <GenericTableScene />
              </slot>
            </template>
            <template v-if="searchTerm || canClearFilters" #actions>
              <VButton v-if="searchTerm" variant="gray" rounded data-testid="table-clear-search" @click="clearSearch">
                {{ t("base.table.state.clear_search") }}
              </VButton>
              <VButton v-if="canClearFilters" variant="light" rounded data-testid="table-clear-filters" @click="clearAllFilters">
                {{ t("base.table.state.clear_filters") }}
              </VButton>
            </template>
          </VEmptyState>
        </slot>
        <slot v-else name="empty">
          <VEmptyState
            :title="emptyTitleText"
            :text="emptyTextValue"
            state="empty"
            variant="plain"
            :heading-level="3"
          >
            <template v-if="slots.emptyIcon" #illustration>
              <slot name="emptyIcon" />
            </template>
            <template v-else #scene>
              <slot name="scene" state="empty">
                <GenericTableScene />
              </slot>
            </template>
            <template v-if="slots.emptyTitle" #title>
              <slot name="emptyTitle" />
            </template>
            <template v-if="slots.emptyText" #default>
              <slot name="emptyText" />
            </template>
            <template v-if="slots['empty-actions']" #actions>
              <slot name="empty-actions" />
            </template>
          </VEmptyState>
        </slot>
      </div>
      <slot name="footer" />
    </div>

    <VTableFooter
      v-if="footerVisible"
      :loaded-count="items.length"
      :total-items="footerTotal"
      :fetching-more="isFetchingMore || isFetchingPrevious"
      :items-label="resolvedItemsLabel"
    />
  </div>
</template>

<style lang="scss" scoped>
@reference "../../tailwind-reference.css";

.v-table-container {
  color: var(--text);
  flex-grow: 1;
  height: 100%; /* Ensure the container takes up the full height */
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.v-table-content {
  width: 100%;
  padding: 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
}

.v-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  &.is-virtualized {
    // Keep column widths stable while only a window of rows is rendered.
    table-layout: fixed;
  }
}

.v-table-spacer {
  // Pure scroll-height placeholder; must not react to hover/click.
  pointer-events: none;
  background: transparent !important;
}

.v-table-cell {
  @apply border-t border-gray-200 dark:border-dark-600;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;

  &.is-right {
    text-align: right;

    :deep(.table-heading) {
      justify-content: flex-end;
    }
  }

  &.is-left {
    text-align: left;

    :deep(.table-heading) {
      justify-content: flex-start;
    }
  }

  &.is-center {
    text-align: center;

    :deep(.table-heading) {
      justify-content: center;
    }
  }
}

/* Sticky column header: the rows scroll under it inside the table. 12px/500 gray on gray-50. */
.v-table-header-cell {
  @apply border-t-0 border-b border-gray-200 bg-gray-50 text-gray-700 dark:border-dark-600 dark:bg-dark-700 dark:text-gray-400;
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 2px 14px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
  vertical-align: middle;

  &.is-right {
    text-align: right;
  }

  &.is-left {
    text-align: left;
  }

  &.is-center {
    text-align: center;
  }
}

/* The first body row sits right under the header's bottom border. */
tbody > tr:first-child > .v-table-cell {
  border-top-width: 0;
}

.v-table-row {
  transition: background-color 0.15s ease;

  &.is-clickable {
    cursor: pointer;
  }

  &.is-clickable:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
}

.v-table-card.is-clickable:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}

.v-table-body-row {
  @apply hover:bg-gray-50 dark:hover:bg-dark-900/40;

  &.is-selected {
    @apply bg-primary-50/60 dark:bg-primary-900/20;
  }
}

.v-table-group-cell {
  @apply bg-gray-50 text-gray-700 dark:bg-dark-700 dark:text-gray-400;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
}

.v-table-state-cell {
  padding: 0;
  white-space: normal;
}

.v-table-select-cell {
  width: 40px !important;
  min-width: 40px;
  max-width: 40px;
  text-align: center;
  user-select: none;
}

@media (max-width: 768px) {
  .v-table-container {
    height: auto;
    max-height: 84vh;
  }

  .v-table-content {
    padding: 0;
  }

  .v-table-cell {
    padding: 8px 12px;
  }
}
</style>
